<script lang="ts">
	//Services
	import { format } from '../../../../../services/i18n';
	//Helpers
	import { formatSnakeCaseToTitle } from '../../../../../helpers/utilities';
	//Design system components
	import { Container, Radio, Text } from '@Shelly/shelly-design-system';
	import { afterUpdate } from 'svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { InputOutputOptions } from './input_output_types';

	export let typeValue: string,
		initialStateValue: string,
		initialStateOptions: InputOutputOptions,
		modeValue: string,
		mandatoryUpdate: boolean,
		matchInputOnlyOnSwitchFollow = false;

	const setInitialState = async (_initial_state_value) => {
		initialStateValue = _initial_state_value;
	};

	let disabled = {};

	const isSwitchOnlyInitialStateBlocked = () => {
		if (matchInputOnlyOnSwitchFollow) {
			return !(typeValue === 'switch' && modeValue === 'follow');
		}
		return (
			typeValue === 'button' ||
			(typeValue === 'switch' && (modeValue === 'flip' || modeValue === 'detached'))
		);
	};

	afterUpdate(async () => {
		//Finally using afterUpdate because changing the type value causes dynamic changes which can't be handled in beforeUpdate
		//this lifecycle is going to evaluated every time when typeValue is changed
		//the object disabled is changed with new values for every type option
		Array.isArray(Object?.entries(initialStateOptions))
			? Object?.entries(initialStateOptions)?.map((option, i) => {
					disabled[i] = option?.[1]?.type !== typeValue && option?.[1]?.type !== 'any';
				})
			: undefined;

		const blockSwitchOnlyInitialState = isSwitchOnlyInitialStateBlocked();

		//Detached also gives an error
		let selectedTypeIndex = Object.keys(initialStateOptions).indexOf(initialStateValue);
		//selectedTypeIndex is the index of the previous selected type option
		let isSelectedTypedDisabled = disabled[selectedTypeIndex];
		//isSelectedTypedDisabled checks if the previous selected index is enabled or disabled
		//if is false, then this means that is valid option for the button/switch
		// if not, we have to find and select first available option

		let availableIndexes = [];
		let firstAvailableOption;

		if (isSelectedTypedDisabled) {
			Object.entries(disabled).forEach(([key, value]) => {
				if (!value) {
					//record all available indexes
					availableIndexes.push(key);
				}
			});
			// buttonModeOptions are all relay type options represent in object with keys, keys represent the types
			// availableIndexes[0] returns first available index(0, 1, or 2 and so on)
			// Object.keys(buttonModeOptions)[availableIndexes[0]] select first available type
			firstAvailableOption = Object.keys(initialStateOptions)[availableIndexes[0]];
			setInitialState(firstAvailableOption);
		}
		if (blockSwitchOnlyInitialState) {
			Object.entries(initialStateOptions).forEach(([, opt], i) => {
				if (opt?.type === 'switch') {
					disabled[i] = true;
				}
			});
			const selectedSwitchOnlyBlocked =
				initialStateOptions[initialStateValue]?.type === 'switch' && !mandatoryUpdate;
			if (selectedSwitchOnlyBlocked) {
				setInitialState(Object.keys(initialStateOptions)[0]);
			}
		}

		/**
		 * Note:
		 * Short description of switch and button mode combinations:
		 * #* 1. switch:
		 * #- relay types:
		 * #-- follow * #-- allowed initial state: on, off, restore_last,match_input
		 * #-- flip * #-- allowed initial state: on, off, restore_last
		 * #-- detached * #-- allowed initial state: on, off, restore_last
		 * #-- activate  * #--- allowed initial state: on, off, restore_last, match_input
		 * #* 2. button:
		 * #- relay types:
		 * #-- momentary
		 * #-- detached
		 * #-- activate * #-- For all relay types allowed initial state: on, off, restore_last
		 *
		 * this is covered in https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2529
		 */
	});
</script>

<Container type="base" class={$showNewLayout ? 'mb-l' : 'mb-s'} padding="xs">
	<Text class={`text-bold ${$showNewLayout ? 'mb-m' : 'mb-s'}`}>
		{formatSnakeCaseToTitle($format('init_state_label'))}
	</Text>
	{#each Object.entries(initialStateOptions) as option, i}
		<Radio
			size="m"
			bind:selected={initialStateValue}
			value={option[0]}
			class={$showNewLayout ? 'mb-s' : ''}
			disabled={disabled[i]}
			on:click={() => setInitialState(option[0])}
			id="radio-mu4aojh"
			info={$showNewLayout ? option[1]?.description : undefined}
		>
			{$showNewLayout ? option[1]?.title : option[1]?.text}
		</Radio>
	{/each}
</Container>
